import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Visit } from '@/types/visit.types';
import { visitsService } from '@/services/visits.service';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { VisitStatusBadge, TokenEntryModal } from '@/components/visits';

export const VisitDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [visit, setVisit] = useState<Visit | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Token modal state
  const [showTokenModal, setShowTokenModal] = useState(false);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [isCompletingVisit, setIsCompletingVisit] = useState(false);

  // Reschedule modal state
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleTime, setRescheduleTime] = useState('');
  const [isRescheduling, setIsRescheduling] = useState(false);

  const fetchVisit = useCallback(async () => {
    if (!id) return;

    try {
      setIsLoading(true);
      setError(null);
      const response = await visitsService.getById(id);
      // API interceptor unwraps { success: true, data: {...} } to just the data
      // Backend returns { visit: {...} } so extract the visit object
      const visitData = response.data?.visit || response.data;
      setVisit(visitData);

      // Check if we should open reschedule modal
      if (searchParams.get('action') === 'reschedule') {
        setShowRescheduleModal(true);
      }
    } catch (err) {
      setError('Failed to load visit details');
      console.error('Error fetching visit:', err);
    } finally {
      setIsLoading(false);
    }
  }, [id, searchParams]);

  useEffect(() => {
    fetchVisit();
  }, [fetchVisit]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatTime = (timeString?: string) => {
    if (!timeString) return 'Not specified';
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  // Actions
  const handleCompleteVisit = () => {
    setTokenError(null);
    setShowTokenModal(true);
  };

  const handleTokenSubmit = async (token: string) => {
    if (!id) return;

    try {
      setIsCompletingVisit(true);
      setTokenError(null);
      await visitsService.complete(id, token);
      await fetchVisit();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      const errorMessage = error.response?.data?.message || 'Invalid or expired token';
      setTokenError(errorMessage);
      throw err;
    } finally {
      setIsCompletingVisit(false);
    }
  };

  const handleReschedule = async () => {
    if (!id || !rescheduleDate) return;

    try {
      setIsRescheduling(true);
      await visitsService.reschedule(id, rescheduleDate, rescheduleTime || undefined);
      setShowRescheduleModal(false);
      await fetchVisit();
    } catch (err) {
      console.error('Error rescheduling visit:', err);
      toast.error('Failed to reschedule visit');
    } finally {
      setIsRescheduling(false);
    }
  };

  const handleCancel = async () => {
    if (!id || !confirm('Are you sure you want to cancel this visit?')) return;

    try {
      await visitsService.cancel(id);
      await fetchVisit();
    } catch (err) {
      console.error('Error cancelling visit:', err);
      toast.error('Failed to cancel visit');
    }
  };

  const handleMarkNoShow = async () => {
    if (!id || !confirm('Mark this visit as No Show?')) return;

    try {
      await visitsService.markNoShow(id);
      await fetchVisit();
    } catch (err) {
      console.error('Error marking no show:', err);
      toast.error('Failed to mark as no show');
    }
  };

  const handleCall = () => {
    if (visit) {
      window.open(`tel:${visit.lead.phone}`, '_self');
    }
  };

  const handleWhatsApp = () => {
    if (visit) {
      window.open(`https://wa.me/${visit.lead.phone.replace(/[^0-9]/g, '')}`, '_blank');
    }
  };

  // Status timeline data
  const getStatusTimeline = () => {
    const baseTimeline = [
      {
        status: 'Created',
        date: visit?.created_at || '',
        active: true,
        color: 'bg-secondary-400',
      },
    ];

    if (visit?.status === 'COMPLETED') {
      return [
        ...baseTimeline,
        {
          status: 'Scheduled',
          date: visit.scheduled_date,
          active: true,
          color: 'bg-blue-500',
        },
        {
          status: 'Completed',
          date: visit.completed_at || '',
          active: true,
          color: 'bg-green-500',
        },
      ];
    }

    if (visit?.status === 'CANCELLED') {
      return [
        ...baseTimeline,
        {
          status: 'Cancelled',
          date: '',
          active: true,
          color: 'bg-red-500',
        },
      ];
    }

    if (visit?.status === 'NO_SHOW') {
      return [
        ...baseTimeline,
        {
          status: 'Scheduled',
          date: visit.scheduled_date,
          active: true,
          color: 'bg-blue-500',
        },
        {
          status: 'No Show',
          date: '',
          active: true,
          color: 'bg-orange-500',
        },
      ];
    }

    if (visit?.status === 'RESCHEDULED' || visit?.status === 'SCHEDULED') {
      return [
        ...baseTimeline,
        {
          status: 'Scheduled',
          date: visit.scheduled_date,
          active: true,
          color: 'bg-blue-500',
        },
        {
          status: 'Awaiting Visit',
          date: '',
          active: false,
          color: 'bg-secondary-300',
        },
      ];
    }

    return baseTimeline;
  };

  // Loading State
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="flex flex-col items-center space-y-4">
          <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
          <p className="text-secondary-600">Loading visit details...</p>
        </div>
      </div>
    );
  }

  // Error State
  if (error || !visit) {
    return (
      <div className="text-center py-24">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 mb-4">
          <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-secondary-900 mb-2">
          {error || 'Visit not found'}
        </h3>
        <p className="text-secondary-600 mb-4">
          We could not load the visit details.
        </p>
        <div className="flex items-center justify-center gap-3">
          <Button variant="outline" onClick={() => navigate('/visits')}>
            Back to Visits
          </Button>
          <Button onClick={fetchVisit}>Try Again</Button>
        </div>
      </div>
    );
  }

  const isScheduled = visit.status === 'SCHEDULED';

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <button
        onClick={() => navigate('/visits')}
        className="inline-flex items-center text-sm text-secondary-600 hover:text-secondary-900"
      >
        <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to Visits
      </button>

      {/* Header */}
      <div className="bg-white rounded-xl border border-secondary-200 shadow-sm p-6">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-2xl font-bold text-secondary-900">
                Visit #{visit.visit_number}
              </h1>
              <VisitStatusBadge status={visit.status} />
            </div>
            <p className="text-secondary-500">
              Scheduled for {formatDate(visit.scheduled_date)}
              {visit.scheduled_time && ` at ${formatTime(visit.scheduled_time)}`}
            </p>
          </div>

          {/* Actions based on status */}
          {isScheduled && (
            <div className="flex flex-wrap gap-2">
              <Button onClick={handleCompleteVisit} size="sm">
                Complete with Token
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowRescheduleModal(true)}
                className="text-purple-600 border-purple-300 hover:bg-purple-50"
              >
                Reschedule
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleMarkNoShow}
                className="text-orange-600 border-orange-300 hover:bg-orange-50"
              >
                Mark No Show
              </Button>
              <Button variant="danger" size="sm" onClick={handleCancel}>
                Cancel Visit
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Lead Info Card */}
        <Card>
          <h3 className="text-lg font-semibold text-secondary-900 mb-4">Lead Information</h3>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-secondary-500">Name</p>
              <p className="text-sm font-medium text-secondary-900 mt-1">{visit.lead.name}</p>
            </div>
            <div>
              <p className="text-sm text-secondary-500">Phone</p>
              <p className="text-sm font-medium text-secondary-900 mt-1">{visit.lead.phone}</p>
            </div>
            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleCall}
                leftIcon={
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                }
              >
                Call
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleWhatsApp}
                className="text-green-600 border-green-300 hover:bg-green-50"
                leftIcon={
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                  </svg>
                }
              >
                WhatsApp
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(`/leads/${visit.lead.id}`)}
              >
                View Lead
              </Button>
            </div>
          </div>
        </Card>

        {/* Project Info Card */}
        <Card>
          <h3 className="text-lg font-semibold text-secondary-900 mb-4">Project Information</h3>
          {visit.sales_kit ? (
            <div className="space-y-4">
              <div>
                <p className="text-sm text-secondary-500">Project Name</p>
                <p className="text-sm font-medium text-secondary-900 mt-1">{visit.sales_kit.name}</p>
              </div>
              <div>
                <p className="text-sm text-secondary-500">Location</p>
                <p className="text-sm font-medium text-secondary-900 mt-1">{visit.sales_kit.location}</p>
              </div>
            </div>
          ) : (
            <p className="text-sm text-secondary-500">No project assigned to this visit</p>
          )}
        </Card>

        {/* Status Timeline */}
        <Card className="lg:col-span-2">
          <h3 className="text-lg font-semibold text-secondary-900 mb-4">Status Timeline</h3>
          <div className="flex items-start space-x-4">
            {getStatusTimeline().map((item, index, arr) => (
              <React.Fragment key={index}>
                <div className="flex flex-col items-center text-center">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center ${item.color}`}
                  >
                    {item.active ? (
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <span className="w-3 h-3 bg-white rounded-full"></span>
                    )}
                  </div>
                  <p className="text-sm font-medium text-secondary-900 mt-2">{item.status}</p>
                  {item.date && (
                    <p className="text-xs text-secondary-500 mt-1">
                      {formatDate(item.date)}
                    </p>
                  )}
                </div>
                {index < arr.length - 1 && (
                  <div className="flex-1 h-0.5 bg-secondary-200 mt-5"></div>
                )}
              </React.Fragment>
            ))}
          </div>
        </Card>

        {/* Completion Details (if completed) */}
        {visit.status === 'COMPLETED' && (
          <Card className="lg:col-span-2">
            <h3 className="text-lg font-semibold text-secondary-900 mb-4">Completion Details</h3>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <p className="text-sm text-secondary-500">Completed At</p>
                <p className="text-sm font-medium text-secondary-900 mt-1">
                  {visit.completed_at ? formatDateTime(visit.completed_at) : '-'}
                </p>
              </div>
              {visit.completion_notes && (
                <div className="sm:col-span-2">
                  <p className="text-sm text-secondary-500">Notes</p>
                  <p className="text-sm font-medium text-secondary-900 mt-1 whitespace-pre-wrap">
                    {visit.completion_notes}
                  </p>
                </div>
              )}
            </div>
          </Card>
        )}

        {/* Visit Details */}
        <Card className="lg:col-span-2">
          <h3 className="text-lg font-semibold text-secondary-900 mb-4">Visit Details</h3>
          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <p className="text-sm text-secondary-500">Assigned Broker</p>
              <p className="text-sm font-medium text-secondary-900 mt-1">
                {visit.broker.full_name}
              </p>
            </div>
            <div>
              <p className="text-sm text-secondary-500">Scheduled Date</p>
              <p className="text-sm font-medium text-secondary-900 mt-1">
                {formatDate(visit.scheduled_date)}
              </p>
            </div>
            <div>
              <p className="text-sm text-secondary-500">Scheduled Time</p>
              <p className="text-sm font-medium text-secondary-900 mt-1">
                {formatTime(visit.scheduled_time)}
              </p>
            </div>
            <div>
              <p className="text-sm text-secondary-500">Created At</p>
              <p className="text-sm font-medium text-secondary-900 mt-1">
                {formatDateTime(visit.created_at)}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Token Entry Modal */}
      <TokenEntryModal
        isOpen={showTokenModal}
        onClose={() => {
          setShowTokenModal(false);
          setTokenError(null);
        }}
        onSubmit={handleTokenSubmit}
        visitNumber={visit.visit_number}
        isLoading={isCompletingVisit}
        error={tokenError}
      />

      {/* Reschedule Modal */}
      {showRescheduleModal && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black bg-opacity-50"
            onClick={() => setShowRescheduleModal(false)}
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div
              className="w-full max-w-md bg-white rounded-2xl shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-5 border-b border-secondary-200">
                <h3 className="text-lg font-semibold text-secondary-900">Reschedule Visit</h3>
                <p className="text-sm text-secondary-500 mt-1">
                  Select a new date and time for this visit
                </p>
              </div>
              <div className="px-6 py-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1.5">
                    New Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={rescheduleDate}
                    onChange={(e) => setRescheduleDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-2.5 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1.5">
                    New Time (Optional)
                  </label>
                  <input
                    type="time"
                    value={rescheduleTime}
                    onChange={(e) => setRescheduleTime(e.target.value)}
                    className="w-full px-4 py-2.5 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>
              <div className="px-6 py-4 bg-secondary-50 border-t border-secondary-200 rounded-b-2xl flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowRescheduleModal(false)}
                  disabled={isRescheduling}
                  fullWidth
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleReschedule}
                  disabled={!rescheduleDate || isRescheduling}
                  isLoading={isRescheduling}
                  fullWidth
                >
                  Reschedule
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default VisitDetail;
